import { memo, useCallback, useRef, useState } from 'react';
import {
  REQUIREMENT_FACETS,
  REQUIREMENT_FACET_ORDER,
  type RequirementFacet,
} from '@scoutaround/script-breakdown-shared';
import { ConversationEntry } from './conversations/ConversationEntry.js';
import { SceneConversationEntry } from './conversations/ConversationDrawer.js';
import { StatusPill } from './ui/StatusPill.js';
import { snapToWords } from '../lib/snapToWords.js';

// `snapToWords` moved to `lib/snapToWords.ts` (the scene breakdown reader uses it too); this
// re-export keeps every existing import working.
export { snapToWords };

/** A run of screenplay text, optionally carrying what it was tagged as. */
export interface SceneSpan {
  readonly text: string;
  /** Tagged as part of the location requirement — rendered in the facet's. */
  readonly facet?: RequirementFacet;
}

export interface SceneLine {
  readonly kind: 'ACTION' | 'CHARACTER' | 'DIALOGUE' | 'PARENTHETICAL';
  readonly spans: readonly SceneSpan[];
}

export interface SceneCardScene {
  readonly id: string;
  readonly sceneNumber: string | null;
  /** Where it sits in the screenplay — what the number falls back to. */
  readonly ordinal?: number;
  readonly slugline: string | null;
  /** INT / EXT and the time of day, as the heading states them — the scene's identity at a glance. */
  readonly intExt?: string | null;
  readonly timeOfDay?: string | null;
  /** False when extraction resolved no location noun for the scene. */
  readonly locationRecognised: boolean;
  /** Why not — shown verbatim, because "not recognised" alone is not actionable. */
  readonly recognitionNote?: string | null;
  readonly lines: readonly SceneLine[];
  /**
   * The requirements extraction attached to this scene, with their status.
   * Absent on callers that render the sheet without the decision strip.
   */
  readonly requirements?: readonly SceneCardRequirement[];
}

/**
 * What a phrase selected in the text is marked as: a value of the scene's location requirement.
 * (Production elements are tagged in the scene breakdown workspace, `components/elements/`.)
 */
export type TagTarget = { readonly kind: 'REQUIREMENT'; readonly facet: RequirementFacet };

/**
 * A location requirement this scene produced, as the producer decides on it.
 *
 * THE SAME ROW the Sourcing list shows — `status` is the requirement's own
 * status, and the decision buttons call the same approve and dismiss verbs.
 * Nothing here is a copy that would need reconciling later.
 */
export interface SceneCardRequirement {
  readonly id: string;
  readonly title: string;
  /** PROPOSED | APPROVED | DISMISSED. */
  readonly status: string;
  readonly confidenceBand?: string;
  /** The other scenes this requirement covers — a decision here reaches them. */
  readonly sharedWithSceneNumbers?: readonly string[];
  /**
   * What the location must be like — the values on the requirement, each with
   * the question it answers (`facet`) and how badly it is wanted (`kind`).
   * Absent where the caller does not hold the rows.
   */
  readonly constraints?: readonly SceneCardConstraint[];
  /** The creative brief the reading wrote for the place, when it wrote one. */
  readonly creativeDescription?: string | null;
}

export interface SceneCardConstraint {
  readonly id: string;
  /** MUST_HAVE | NICE_TO_HAVE | EXCLUDE | INFORMATIONAL. */
  readonly kind: string;
  /** One of `REQUIREMENT_FACET_ORDER`, or null for a value filed under none. */
  readonly facet: string | null;
  readonly label: string;
}

export type RequirementDecision = 'APPROVE' | 'DISMISS';

/**
 * One scene, as the producer works its LOCATION requirements: the scene's identity, the
 * requirements the reading proposed for it with what each must be like, the affordance to
 * disagree, and the screenplay as a reference with words to tag as a value of the location.
 *
 * The detailed breakdown — cast, props, costumes and the rest, scene by scene — is its own
 * workspace since 2026-11-05 (`components/elements/`); this card no longer carries a sheet.
 *
 * NOT RECOGNISED IS A STATE, NOT AN ERROR. A scene whose location noun did not
 * resolve — "the crossing", with no place named — is the normal, frequent case
 * in a real screenplay. It is shown as a fact with its reason and a way to fix
 * it, not as a failure, because the producer is the one who knows what "the
 * crossing" is.
 *
 * THE FACET COLOURS ARE THE CONTRACT'S (`REQUIREMENT_FACETS`): what the place itself is, how it
 * must look, what it must physically have, what must be true to shoot there.
 */
/**
 * The selection, restricted to ONE card's own requirements.
 *
 * The screen holds the whole selection in one array, because a location shared
 * by four scenes must show one state on all four and a merge spans scenes. That
 * array's identity changes on every tick, which without this would re-render
 * every card to change one checkbox. A card only cares about the ids it draws.
 */
const selectionHere = (
  scene: SceneCardScene,
  selected: readonly string[] | undefined,
): string =>
  (scene.requirements ?? [])
    .filter((r) => selected?.includes(r.id) ?? false)
    .map((r) => r.id)
    .join(',');

function SceneCardBase({
  scene,
  projectId,
  onTag,
  onAddRequirement,
  onDecideRequirement,
  onRestoreRequirement,
  selectedRequirementIds,
  onToggleRequirementSelected,
  decideRefusal = null,
}: {
  scene: SceneCardScene;
  /** When given, the scene and its requirements carry a "Discuss" control (ADR 0021). */
  projectId?: string;
  /** A producer marked a selection as a value of the scene's location requirement. */
  onTag?: (sceneId: string, selection: string, target: TagTarget) => void;
  onAddRequirement?: (sceneId: string) => void;
  /** "We need this location" / "We don't need this" on one of the scene's requirements. */
  onDecideRequirement?: (requirementId: string, decision: RequirementDecision) => void;
  /** Undo "We don't need this": a dismissed requirement back to awaiting a decision. */
  onRestoreRequirement?: (requirementId: string) => void;
  /**
   * WHICH REQUIREMENTS THE PRODUCER HAS PICKED OUT, for merging or splitting.
   *
   * Held by the screen, not by the card: a location shared by four scenes must
   * show the same tick on all of them, and a merge is precisely the operation
   * that spans scenes.
   */
  selectedRequirementIds?: readonly string[];
  onToggleRequirementSelected?: (requirementId: string) => void;
  /**
   * Why this person cannot decide on a location, in words, resolved once by the
   * screen. Null means they can. The hint is never the gate (`requirePermission`
   * on the server is), and an unknown answer is "they can".
   */
  decideRefusal?: string | null;
}) {
  /**
   * THE TAGGER IS ANCHORED TO THE WORDS, not stacked above them: opening it in
   * the card's flow pushed the screenplay down and the phrase jumped out from
   * under the cursor. `top`/`left` are measured against the card.
   */
  const [selection, setSelection] = useState<{
    text: string;
    top: number;
    left: number;
  } | null>(null);
  const cardRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // A reference here, folded: the requirement is what is being reviewed.
  const [scriptOpen, setScriptOpen] = useState(false);

  /**
   * The requirement a facet would attach to: exactly one, or none. A scene with
   * two location requirements gives the gesture no unambiguous target, and
   * picking the first would file a demand against whichever was extracted first.
   */
  const requirementId =
    scene.requirements !== undefined && scene.requirements.length === 1 ? (scene.requirements[0]?.id ?? null) : null;

  /**
   * The popover opens from a real text selection rather than a click target,
   * because the unit a producer wants to tag is a phrase they choose.
   */
  const captureSelection = useCallback(() => {
    const sel = window.getSelection();
    const body = bodyRef.current;

    // A stray click clears the selection and would otherwise flicker the
    // popover open and shut on every mouseup in the text.
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || body === null) return;

    const range = sel.getRangeAt(0);
    if (!body.contains(range.commonAncestorContainer)) return;

    // Where the selection sits in the scene's text, measured rather than guessed.
    const upToStart = range.cloneRange();
    upToStart.selectNodeContents(body);
    upToStart.setEnd(range.startContainer, range.startOffset);

    const full = body.textContent ?? '';
    const start = upToStart.toString().length;
    const snapped = snapToWords(full, start, start + range.toString().length);

    if (snapped === '') return;

    // POSITION IS A NICETY; OPENING IS NOT. `Range.getBoundingClientRect` is not
    // implemented everywhere a component is rendered (jsdom has no layout), and
    // letting that throw took the whole menu with it.
    const frame = cardRef.current?.getBoundingClientRect();
    const rect =
      typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : null;

    setSelection({
      text: snapped,
      top: frame && rect ? rect.bottom - frame.top + 8 : 0,
      left: frame && rect ? Math.max(8, Math.min(rect.left - frame.left, frame.width - 340)) : 0,
    });
  }, []);

  const tag = (target: TagTarget) => {
    if (selection === null) return;
    onTag?.(scene.id, selection.text, target);
    setSelection(null);
  };

  const recognised = scene.locationRecognised;
  const sceneFacts = [scene.intExt, scene.timeOfDay].filter(
    (fact): fact is string => typeof fact === 'string' && fact.trim() !== '',
  );

  return (
    <article
      ref={cardRef}
      data-testid={`scene-card-${scene.id}`}
      data-recognised={recognised ? '1' : '0'}
      className="rq-card"
    >
      {/* Which scene: number, heading, and where and when it plays. */}
      <header className="rq-card-head">
        <span data-testid="scene-card-number" className="rq-card-num">
          {scene.sceneNumber ?? (scene.ordinal === undefined ? '—' : String(scene.ordinal))}
        </span>

        <span className="rq-card-id">
          <span data-testid="scene-card-slugline" className="rq-card-slug">
            {scene.slugline ?? 'Untitled scene'}
          </span>
          {sceneFacts.length === 0 ? null : (
            <span className="rq-card-facts" data-testid="scene-card-facts">
              {sceneFacts.map((fact) => (
                <span key={fact} className="chip">
                  {fact}
                </span>
              ))}
            </span>
          )}
        </span>

        {recognised ? null : (
          <span className="rq-flag" data-kind="NOT_RECOGNISED" data-testid="scene-card-unrecognised">
            Not recognised
          </span>
        )}

        {projectId ? (
          <span className="rq-card-discuss">
            <SceneConversationEntry
              projectId={projectId}
              sceneId={scene.id}
              label={`Sc. ${scene.sceneNumber ?? (scene.ordinal === undefined ? '—' : String(scene.ordinal))}${scene.slugline ? ` — ${scene.slugline}` : ''}`}
            />
          </span>
        ) : null}
      </header>

      {/* The decision: is this a location the production needs, and what must it be like? */}
      <section className="rq-card-reqs" aria-label="Location requirements">
        <p className="colh rq-card-section">
          {`Location requirement${(scene.requirements ?? []).length === 1 ? '' : 's'}`}
        </p>

        {scene.requirements !== undefined && scene.requirements.length > 0 ? (
          <div data-testid="scene-card-requirements" className="rq-reqs">
            {scene.requirements.map((r) => (
              <div
                key={r.id}
                data-testid={`scene-card-requirement-${r.id}`}
                data-status={r.status}
                className="rq-req"
              >
                <div className="rq-req-line">
                  {/* THE THING THAT MAKES MERGE AND SPLIT REACHABLE. Offered only on
                      live rows: a dismissed location is one the production said it
                      does not need, and a superseded one already lost a merge. */}
                  {onToggleRequirementSelected !== undefined &&
                  r.status !== 'DISMISSED' &&
                  r.status !== 'SUPERSEDED' ? (
                    <input
                      type="checkbox"
                      className="rq-req-pick"
                      data-testid={`scene-card-select-requirement-${r.id}`}
                      // NAMED, not just "SELECT": read out of context they would
                      // otherwise announce identically.
                      aria-label={`Select ${r.title} for merging or splitting`}
                      disabled={decideRefusal !== null}
                      title={decideRefusal ?? 'Select for merging or splitting'}
                      checked={selectedRequirementIds?.includes(r.id) ?? false}
                      onChange={() => onToggleRequirementSelected(r.id)}
                    />
                  ) : null}

                  <span className="rq-req-title">{r.title}</span>

                  <RequirementStatusTag status={r.status} />

                  {r.confidenceBand !== undefined && r.confidenceBand !== 'HIGH' ? (
                    <span className="rq-flag" data-kind="LOW_CONFIDENCE">
                      Low confidence
                    </span>
                  ) : null}

                  {projectId ? (
                    <ConversationEntry projectId={projectId} anchorType="REQUIREMENT" anchorId={r.id} label={r.title} compact />
                  ) : null}

                  {/* Approval and searching are SEPARATE decisions, so no search
                      starts here. KEEPING AND DISCARDING ARE BOTH HERE (owner,
                      2026-10-27), with Restore beside a discard. */}
                  <span className="rq-req-actions">
                    {r.status !== 'APPROVED' && r.status !== 'DISMISSED' ? (
                      <button
                        type="button"
                        className="ctl"
                        data-testid={`scene-card-approve-${r.id}`}
                        disabled={decideRefusal !== null}
                        title={decideRefusal ?? undefined}
                        onClick={() => onDecideRequirement?.(r.id, 'APPROVE')}
                      >
                        We need this location
                      </button>
                    ) : null}
                    {r.status !== 'DISMISSED' && r.status !== 'SUPERSEDED' ? (
                      <button
                        type="button"
                        className="ctl"
                        data-testid={`scene-card-dismiss-${r.id}`}
                        disabled={decideRefusal !== null}
                        title={decideRefusal ?? undefined}
                        onClick={() => onDecideRequirement?.(r.id, 'DISMISS')}
                      >
                        We don&rsquo;t need this
                      </button>
                    ) : null}
                    {r.status === 'DISMISSED' && onRestoreRequirement !== undefined ? (
                      <button
                        type="button"
                        className="ctl"
                        data-testid={`scene-card-restore-${r.id}`}
                        disabled={decideRefusal !== null}
                        title={decideRefusal ?? undefined}
                        onClick={() => onRestoreRequirement(r.id)}
                      >
                        Restore
                      </button>
                    ) : null}
                  </span>
                </div>

                {r.sharedWithSceneNumbers !== undefined && r.sharedWithSceneNumbers.length > 0 ? (
                  <p className="rq-req-shared" data-testid={`scene-card-shared-${r.id}`}>
                    {`Also scene${r.sharedWithSceneNumbers.length === 1 ? '' : 's'} ${r.sharedWithSceneNumbers.join(', ')} — a decision here applies there too`}
                  </p>
                ) : null}

                <RequirementValues requirement={r} />
              </div>
            ))}
          </div>
        ) : (
          <p className="mm rq-card-none" data-testid="scene-card-note">
            {scene.recognitionNote ?? 'No location requirement on this scene yet.'}
          </p>
        )}

        <button
          type="button"
          className="ctl rq-card-add"
          data-testid="scene-card-add-requirement"
          disabled={decideRefusal !== null}
          title={decideRefusal ?? undefined}
          onClick={() => onAddRequirement?.(scene.id)}
        >
          + Add a requirement to this scene
        </button>
      </section>

      {/* The tagging popover — where a human correction begins. */}
      {selection === null ? null : (
        <div
          data-testid="scene-card-tagger"
          role="dialog"
          aria-label={`Mark “${selection.text}” as`}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSelection(null);
          }}
          style={{
            position: 'absolute',
            top: selection.top,
            left: selection.left,
            zIndex: 20,
            width: 'min(520px, calc(100% - 16px))',
            maxHeight: '60vh',
            overflowY: 'auto',
            background: 'var(--color-bg)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-neutral-300)',
            borderRadius: 2,
            boxShadow: '0 12px 32px rgba(35, 35, 35, 0.18)',
            padding: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              padding: '10px 14px',
              borderBottom: '1px solid var(--color-divider)',
              background: 'var(--color-surface)',
            }}
          >
            <span
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              &ldquo;{selection.text}&rdquo;
            </span>
            <span className="mm" style={{ fontSize: 12 }}>
              mark as
            </span>
            <button
              type="button"
              data-testid="scene-card-tagger-close"
              aria-label="Cancel tagging"
              onClick={() => {
                setSelection(null);
              }}
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 0,
                color: 'inherit',
                fontSize: 18,
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              ×
            </button>
          </div>

          {/* WHAT THE LOCATION MUST BE. One per row with its own note: "which do I
              want" is answered by the note rather than by guessing from a label. */}
          <div style={{ padding: '12px 14px' }}>
            <p className="flbl" style={{ margin: '0 0 8px' }}>
              What the location must be
            </p>
            {requirementId === null ? (
              <p
                className="mm"
                data-testid="tag-facet-unavailable"
                style={{ fontSize: 12.5, margin: 0, lineHeight: 1.6 }}
              >
                {scene.requirements === undefined || scene.requirements.length === 0
                  ? 'This scene has no location requirement yet — add one from the scene list, and these become available.'
                  : 'This scene carries more than one location requirement, so there is no single one to add this to. Add it where the requirement is edited.'}
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {REQUIREMENT_FACET_ORDER.map((facet) => {
                  const meta = REQUIREMENT_FACETS[facet];

                  return (
                    <button
                      key={facet}
                      type="button"
                      data-testid={`tag-facet-${facet}`}
                      onClick={() => {
                        tag({ kind: 'REQUIREMENT', facet });
                      }}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '10px 1fr',
                        gap: 10,
                        alignItems: 'start',
                        textAlign: 'left',
                        background: 'var(--color-bg)',
                        border: '1px solid var(--color-divider)',
                        borderLeft: `4px solid ${meta.colour}`,
                        padding: '8px 10px',
                        cursor: 'pointer',
                        font: 'inherit',
                        color: 'inherit',
                      }}
                    >
                      <i
                        aria-hidden="true"
                        style={{ width: 10, height: 10, background: meta.colour, marginTop: 4 }}
                      />
                      <span>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                          {meta.label}
                        </span>
                        <span className="mm" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
                          {meta.use}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* The screenplay: a REFERENCE here, folded — the requirement is the thing
          being reviewed, and the text is one click away. */}
      <details
        className="rq-card-script"
        data-testid="scene-card-script"
        open={scriptOpen}
        onToggle={(e) => {
          setScriptOpen(e.currentTarget.open);
        }}
      >
        <summary data-testid="scene-card-script-toggle">
          <span className="colh">Screenplay excerpt</span>
          <span className="mm rq-card-script-meta">
            Read-only · select words to mark what the location must be
          </span>
        </summary>

        <div
          ref={bodyRef}
          data-testid="scene-card-text"
          onMouseUp={captureSelection}
          style={{
            padding: '20px 24px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 13,
            lineHeight: 1.9,
          }}
        >
          {scene.lines.map((line, lineIndex) => (
            <p
              // Screenplay lines have no stable identity of their own; they are a
              // rendering of a text, and the text is the identity.
              key={`${scene.id}-line-${String(lineIndex)}`}
              style={{
                margin: 0,
                textAlign:
                  line.kind === 'CHARACTER' || line.kind === 'DIALOGUE' || line.kind === 'PARENTHETICAL'
                    ? 'center'
                    : 'left',
              }}
            >
              {line.spans.map((span, spanIndex) => (
                <SceneTextSpan key={`${scene.id}-${String(lineIndex)}-${String(spanIndex)}`} span={span} />
              ))}
            </p>
          ))}
        </div>
      </details>
    </article>
  );
}

/**
 * The requirement's status, in the reader's words rather than the enum's, in
 * the workspace's one status vocabulary: awaiting a decision is "ready for
 * review", approved is confirmed, not needed is set aside.
 */
export function RequirementStatusTag({ status }: { status: string }) {
  if (status === 'APPROVED') return <StatusPill state="CONFIRMED" label="Approved" size="sm" />;
  if (status === 'DISMISSED') return <StatusPill state="EXCLUDED" label="Not needed" size="sm" />;
  if (status === 'SUPERSEDED') return <StatusPill state="EXCLUDED" label="Merged" size="sm" />;

  return <StatusPill state="READY_FOR_REVIEW" label="Awaiting your decision" size="sm" />;
}

/** How a value is wanted, as the matching uses it (see `ValueLegend`). */
const KIND_LABEL: Readonly<Record<string, string>> = {
  MUST_HAVE: 'Must',
  NICE_TO_HAVE: 'Nice',
  EXCLUDE: 'Not',
};

/**
 * WHAT THE LOCATION MUST BE LIKE, grouped by the question each value answers.
 *
 * The groups are the facets the tagging surface already uses — the place
 * itself, how it must look, what it must physically have, what must be true to
 * shoot there — read from the contract in its order, so a value tagged from the
 * text lands under the heading the tagger showed. Each chip says how badly it is
 * wanted, because "Must" removes candidates and "Nice" only ranks them. Nothing
 * is drawn for a group with no value: an empty facet here is "nobody said",
 * not "none".
 */
export function RequirementValues({ requirement }: { requirement: SceneCardRequirement }) {
  const constraints = requirement.constraints ?? [];
  const brief = requirement.creativeDescription?.trim() ?? '';
  if (constraints.length === 0 && brief === '') return null;

  const groups = [
    ...REQUIREMENT_FACET_ORDER.map((facet) => ({
      key: facet,
      label: REQUIREMENT_FACETS[facet].label,
      values: constraints.filter((c) => c.facet === facet),
    })),
    {
      key: 'OTHER',
      label: 'Other values',
      values: constraints.filter(
        (c) => c.facet === null || !(REQUIREMENT_FACET_ORDER as readonly string[]).includes(c.facet),
      ),
    },
  ].filter((group) => group.values.length > 0);

  return (
    <div className="rq-values" data-testid={`scene-card-values-${requirement.id}`}>
      {brief === '' ? null : (
        <p className="rq-values-brief" data-testid={`scene-card-brief-${requirement.id}`}>
          {brief}
        </p>
      )}
      {groups.map((group) => (
        <div key={group.key} className="rq-values-group" data-testid={`scene-card-facet-${requirement.id}-${group.key}`}>
          <span className="rq-values-label">{group.label}</span>
          <span className="rq-values-chips">
            {group.values.map((c) => (
              <span key={c.id} className="rq-value" data-kind={c.kind}>
                {KIND_LABEL[c.kind] === undefined ? null : <b>{KIND_LABEL[c.kind]}</b>}
                {c.label}
              </span>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * One run of screenplay text, marked in its facet's colour when it carries one.
 */
function SceneTextSpan({ span }: { span: SceneSpan }) {
  if (span.facet === undefined) return <>{span.text}</>;

  const meta = REQUIREMENT_FACETS[span.facet];

  return (
    <mark
      data-testid="scene-card-mark"
      title={meta.label}
      style={{ background: 'transparent', color: 'inherit', boxShadow: `inset 0 0 0 1.5px ${meta.colour}`, padding: '1px 5px' }}
    >
      {span.text}
    </mark>
  );
}

/**
 * MEMOISED, and deliberately not with the default comparison.
 *
 * Every other prop is compared by identity, which is why the screen hoists its
 * handlers instead of building closures inside the render loop — a fresh
 * closure per card per render would defeat any memo. `selectedRequirementIds`
 * is the exception: it is one array shared by every card, so it changes on
 * every tick and would fail an identity check on all of them. It is compared by
 * what it means for THIS card instead, so ticking a requirement re-renders the
 * cards that draw it and nothing else.
 */
export const SceneCard = memo(SceneCardBase, (prev, next) => {
  const keys = Object.keys(next) as (keyof Parameters<typeof SceneCardBase>[0])[];
  if (Object.keys(prev).length !== keys.length) return false;

  for (const key of keys) {
    if (key === 'selectedRequirementIds') continue;
    if (prev[key] !== next[key]) return false;
  }

  return (
    selectionHere(next.scene, prev.selectedRequirementIds) ===
    selectionHere(next.scene, next.selectedRequirementIds)
  );
});
